import {Injectable} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import * as securityAction from '../actions/security.action';
import {
  IGNORE_SECURITY_ISSUE,
  IgnoreSecurityIssue,
  LOAD_SECURITY_AUTOUPDATE,
  LOAD_SECURITY_SCAN,
  LoadSecurityAutoupdate,
  LoadSecurityScan,
  RESTORE_SECURITY_FILE,
  RESTORE_SECURITY_ISSUE,
  RestoreSecurityFile,
  RestoreSecurityIssue
} from '../actions/security.action';
import {DbResponse} from "../../shared/interceptors/db-response";
import {filter, first, map, mergeMap, switchMap} from 'rxjs/operators';
import {SecurityService} from "../../websites/website/security/security.service";
import {Store} from '@ngrx/store';
import {
  ADD_ACTIVITY,
  AddActivity,
  FinishOngoingActivity,
  LOAD_SECURITY_IGNORED_ISSUES,
  LOAD_SECURITY_SCHEDULE,
  LOAD_SECURITY_SETTINGS,
  LoadSecurityIgnoredIssues,
  LoadSecuritySchedule,
  LoadSecuritySettings,
  RUN_SECURITY_SCAN,
  RUN_SECURITY_SCAN_SUCCESS,
  RunSecurityScan,
  RunSecurityScanSuccess,
  UPDATE_SECURITY_AUTOUPDATE,
  UPDATE_SECURITY_SCHEDULE,
  UPDATE_SECURITY_SETTINGS,
  UpdateOngoingActivityState,
  UpdateSecurityAutoupdate,
  UpdateSecuritySchedule,
  UpdateSecuritySettings
} from '../actions';
import {ActivityAction, ActivityStatus, ActivityType, IActivity} from "../../activities/activity";
import * as ongoingActivityActions from "../actions/ongoing-activities.action";
import {DbStoreState} from "../reducers";
import {SecuritySettingsMapper} from "../../websites/website/security/security-settings-mapper";
import {Config} from "../../config/config";
import {SecurityScheduleMapper} from "../../websites/website/security/security-schedule-mapper";
import {SecurityAutoupdateMapper} from "../../websites/website/security/security-autoupdate-mapper";

@Injectable()
export class SecurityEffect {

  securityPluginInstalled$ = createEffect(() => this._actions$.pipe(
    ofType(ADD_ACTIVITY),
    filter((action: AddActivity) => {
      const activity: IActivity = action.payload;
      const isInstallAndActivate: boolean = activity.action === ActivityAction.INSTALL_ACTIVATE;
      const isDone: boolean = activity.status === ActivityStatus.DONE;
      const productData: any = activity.meta ? activity.meta.product_data : null;
      const isSecurityPlugin: boolean = productData && productData.slug === Config.securityPluginSlug;

      return isInstallAndActivate && isDone && isSecurityPlugin;
    }),
    map((action: AddActivity) => new securityAction.RunSecurityScan(action.payload.websiteId))
  ));
  runSecurityScanDone$ = createEffect(() => this._actions$.pipe(
    ofType(ongoingActivityActions.FINISH_ONGOING_ACTIVITY),
    filter((action: FinishOngoingActivity) => action.payload.entity.action === ActivityAction.SECURITY_SCAN),
    filter((action: FinishOngoingActivity) => action.payload.entity.status === ActivityStatus.DONE),
    map((action: FinishOngoingActivity) => {
      const activity: IActivity = action.payload.entity;

      return new securityAction.RunSecurityScanSuccess({
        websiteId: activity.websiteId,
        securityScan: null
      });
    })
  ));
  runSecurityScanFailed$ = createEffect(() => this._actions$.pipe(
    ofType(ongoingActivityActions.FINISH_ONGOING_ACTIVITY),
    filter((action: FinishOngoingActivity) => action.payload.entity.action === ActivityAction.SECURITY_SCAN),
    filter((action: FinishOngoingActivity) => action.payload.entity.status === ActivityStatus.FAILED),
    map((action: FinishOngoingActivity) => {
      const activity: IActivity = action.payload.entity;

      return new securityAction.RunSecurityScanFail({
        websiteId: activity.websiteId,
        error: activity.message
      });
    })
  ));

  runSecurityScanSuccess$ = createEffect(() => this._actions$.pipe(
    ofType(RUN_SECURITY_SCAN_SUCCESS),
    switchMap((action: RunSecurityScanSuccess) => {
      const websiteId: number = action.payload.websiteId;
      return [new securityAction.LoadSecurityScan(websiteId)];
    })
  ));

  firstRunSecurityScanSuccess$ = createEffect(() => this._actions$.pipe(
    ofType(RUN_SECURITY_SCAN_SUCCESS),
    switchMap((action: RunSecurityScanSuccess) => {
      const websiteId: number = action.payload.websiteId;
      return [new securityAction.LoadSecuritySettings(websiteId)];
    })
  ));
  loadSecurityScan$ = createEffect(() => this._actions$.pipe(
    ofType(LOAD_SECURITY_SCAN),
    mergeMap((action: LoadSecurityScan) => {
      const websiteId: number = action.payload;
      return this._securityService.getSecurityScan(websiteId)
        .pipe(
          filter((response: DbResponse) => !!response),
          first(),
          map((response: DbResponse) => {
            if (response.success) {
              return new securityAction.LoadSecurityScanSuccess({
                websiteId: websiteId,
                securityScan: response.data
              });
            } else {
              return new securityAction.LoadSecurityScanFail({
                websiteId: websiteId,
                error: response.message
              });
            }
          })
        );
    })
  ));
  loadSecurityIgnoredIssues$ = createEffect(() => this._actions$.pipe(
    ofType(LOAD_SECURITY_IGNORED_ISSUES),
    mergeMap((action: LoadSecurityIgnoredIssues) => {
      const websiteId: number = action.payload;
      return this._securityService.getSecurityIgnoredIssues(websiteId)
        .pipe(
          filter((response: DbResponse) => !!response),
          first(),
          map((response: DbResponse) => {
            if (response.success) {
              return new securityAction.LoadSecurityIgnoredIssuesSuccess({
                websiteId: websiteId,
                securityIgnoredIssues: response.data
              });
            } else {
              return new securityAction.LoadSecurityIgnoredIssuesFail({
                websiteId: websiteId,
                error: response.message
              });
            }
          })
        );
    })
  ));
  ignoreSecurityIssue$ = createEffect(() => this._actions$.pipe(
    ofType(IGNORE_SECURITY_ISSUE),
    switchMap((action: IgnoreSecurityIssue) => [
      new securityAction.LoadSecurityScan(action.payload.websiteId),
      new securityAction.LoadSecurityIgnoredIssues(action.payload.websiteId)
    ])
  ));

  restoreSecurityIssue$ = createEffect(() => this._actions$.pipe(
    ofType(RESTORE_SECURITY_ISSUE),
    switchMap((action: RestoreSecurityIssue) => [
      new securityAction.LoadSecurityScan(action.payload.websiteId),
      new securityAction.LoadSecurityIgnoredIssues(action.payload.websiteId)
    ])
  ));

  restoreSecurityFile$ = createEffect(() => this._actions$.pipe(
    ofType(RESTORE_SECURITY_FILE),
    map((action: RestoreSecurityFile) => new securityAction.LoadSecurityScan(action.payload.websiteId))
  ));

  loadSecuritySettings$ = createEffect(() => this._actions$.pipe(
    ofType(LOAD_SECURITY_SETTINGS),
    mergeMap((action: LoadSecuritySettings) => {
      const websiteId: number = action.payload;
      return this._securityService.getSecuritySettings(websiteId)
        .pipe(
          filter((response: DbResponse) => !!response),
          first(),
          map((response: DbResponse) => {
            if (response.success) {
              return new securityAction.LoadSecuritySettingsSuccess({
                websiteId: websiteId,
                securitySettings: response.data
              });
            } else {
              return new securityAction.LoadSecuritySettingsFail({
                websiteId: websiteId,
                error: response.message
              });
            }
          })
        );
    })
  ));
  loadSecuritySchedule$ = createEffect(() => this._actions$.pipe(
    ofType(LOAD_SECURITY_SCHEDULE),
    mergeMap((action: LoadSecuritySchedule) => {
      const websiteId: number = action.payload;
      return this._securityService.getSecuritySchedule(websiteId)
        .pipe(
          filter((response: DbResponse) => !!response),
          first(),
          map((response: DbResponse) => {
            if (response.success) {
              return new securityAction.LoadSecurityScheduleSuccess({
                websiteId: websiteId,
                securitySchedule: response.data
              });
            } else {
              return new securityAction.LoadSecurityScheduleFail({
                websiteId: websiteId,
                error: response.message
              });
            }
          })
        );
    })
  ));
  loadSecurityAutoupdate$ = createEffect(() => this._actions$.pipe(
    ofType(LOAD_SECURITY_AUTOUPDATE),
    mergeMap((action: LoadSecurityAutoupdate) => {
      const websiteId: number = action.payload;
      return this._securityService.getSecurityAutoupdate(websiteId)
        .pipe(
          filter((response: DbResponse) => !!response),
          first(),
          map((response: DbResponse) => {
            if (response.success) {
              return new securityAction.LoadSecurityAutoupdateSuccess({
                websiteId: websiteId,
                securityAutoupdate: response.data.general
              });
            } else {
              return new securityAction.LoadSecurityAutoupdateFail({
                websiteId: websiteId,
                error: response.message
              });
            }
          })
        );
    })
  ));
  updateSecuritySettings$ = createEffect(() => this._actions$.pipe(
    ofType(UPDATE_SECURITY_SETTINGS),
    switchMap((action: UpdateSecuritySettings) => {
      const payload: any = action.payload;
      return this._securityService.updateSecuritySettings(payload.websiteId, SecuritySettingsMapper.generateRequestData(payload.settings))
        .pipe(
          filter((response: DbResponse) => !!response),
          first(),
          map((response: DbResponse) => {
            if (response.success) {
              return new securityAction.UpdateSecuritySettingsSuccess({
                websiteId: payload.websiteId,
                securitySettings: SecuritySettingsMapper.mapResponseData(payload.settings)
              });
            }

            return new securityAction.UpdateSecuritySettingsFail({
              websiteId: payload.websiteId,
              error: response.message
            });
          })
        );
    })
  ));

  updateSecuritySchedule$ = createEffect(() => this._actions$.pipe(
    ofType(UPDATE_SECURITY_SCHEDULE),
    switchMap((action: UpdateSecuritySchedule) => {
      const payload: any = action.payload;
      return this._securityService.updateSecuritySchedule(payload.websiteId, payload.securitySchedule)
        .pipe(
          filter((response: DbResponse) => !!response),
          first(),
          map((response: DbResponse) => {
            if (response.success) {
              return new securityAction.UpdateSecurityScheduleSuccess({
                websiteId: payload.websiteId,
                securitySchedule: SecurityScheduleMapper.mapResponseData(payload.securitySchedule)
              });
            }

            return new securityAction.UpdateSecurityScheduleFail({
              websiteId: payload.websiteId,
              error: response.message
            });
          })
        );
    })
  ));
  updateSecurityAutoupdate$ = createEffect(() => this._actions$.pipe(
    ofType(UPDATE_SECURITY_AUTOUPDATE),
    switchMap((action: UpdateSecurityAutoupdate) => {
      const payload: any = action.payload;
      return this._securityService.updateSecurityAutoupdate(payload.websiteId, SecurityAutoupdateMapper.generateRequestData(payload.securityAutoupdate))
        .pipe(
          filter((response: DbResponse) => !!response),
          first(),
          map((response: DbResponse) => {
            if (response.success) {
              return new securityAction.UpdateSecurityAutoupdateSuccess({
                websiteId: payload.websiteId,
                securityAutoupdate: SecurityAutoupdateMapper.mapResponseData(payload.securityAutoupdate)
              });
            }

            return new securityAction.UpdateSecurityAutoupdateFail({
              websiteId: payload.websiteId,
              error: response.message
            });
          })
        );
    })
  ));

  runSecurityScan$ = createEffect(() => this._actions$.pipe(
    ofType(RUN_SECURITY_SCAN),
    map((action: RunSecurityScan) => {
      const websiteId: number = action.payload;
      const activityId: string = websiteId + 'SecurityScan';

      this._securityService.runSecurityScan(websiteId)
        .pipe(
          filter((response: DbResponse) => !!response),
          first()
        )
        .subscribe((response: DbResponse) => {
          if (!response.success) {
            setTimeout(() => {
              delete this._pendingActivityMap[activityId];

              this._store.dispatch(new ongoingActivityActions.UpdateOngoingActivityState({
                websiteId: websiteId,
                entity: this.generateSecurityScanFailedActivity(activityId, websiteId)
              }));
              this._store.dispatch(new ongoingActivityActions.FinishOngoingActivity({
                websiteId: websiteId,
                entity: this.generateSecurityScanFailedActivity(activityId, websiteId)
              }));
            });
          }
        });

      const activity: IActivity = this.generateSecurityScanPendingActivity(activityId, websiteId);
      this._pendingActivityMap[activityId] = activity;

      return new ongoingActivityActions.UpdateOngoingActivityState({
        websiteId: websiteId,
        entity: activity
      });
    })
  ));

  runSecurtyScanStarted$ = createEffect(() => this._actions$.pipe(
    ofType(ADD_ACTIVITY),
    filter((action: AddActivity) => action.payload.action === ActivityAction.SECURITY_SCAN),
    filter((action: AddActivity) => action.payload.status === ActivityStatus.RUNNING),
    filter((action: AddActivity): boolean => {
      const activity: IActivity = action.payload;
      const activityId: string = activity.websiteId.toString() + 'SecurityScan';
      return !!this._pendingActivityMap[activityId];
    }),
    map((action: AddActivity) => {
      const activity: IActivity = action.payload;
      const activityId: string = activity.websiteId.toString() + 'SecurityScan';

      delete this._pendingActivityMap[activityId];

      return new ongoingActivityActions.RemoveOngoingActivity({
        websiteId: activity.websiteId,
        activityId: activityId
      });
    })
  ));
  private readonly _simulatingActivityMap: { [activityId: string]: IActivity };
  private _pendingActivityMap: any;

  constructor(
    private _store: Store<DbStoreState>,
    private _actions$: Actions,
    private _securityService: SecurityService
  ) {
    this._pendingActivityMap = {};
    this._simulatingActivityMap = {};
  }

  private simulateActivityProgress(baseActivity: IActivity): void {
    this._simulatingActivityMap[baseActivity.id] = baseActivity;

    const interval = setInterval((): void => {
      const previousActivity: IActivity = this._simulatingActivityMap[baseActivity.id];

      if (!previousActivity || previousActivity.progress >= 98) {
        clearInterval(interval);
        return;
      }

      let nextProgress: number;
      if (previousActivity.progress < 50) {
        nextProgress = previousActivity.progress + 3;
      } else if (previousActivity.progress < 80) {
        nextProgress = previousActivity.progress + 2;
      } else {
        nextProgress = previousActivity.progress + 1;
      }

      const activity: IActivity = <IActivity>{};
      activity.id = previousActivity.id;
      activity.websiteId = previousActivity.websiteId;
      activity.service = 'manager';
      activity.type = ActivityType.ACTIVITY;
      activity.action = ActivityAction.SECURITY_SCAN;
      activity.date = new Date();
      activity.status = ActivityStatus.RUNNING;
      activity.progress = nextProgress;
      activity.message = '';
      activity.meta = JSON.parse(JSON.stringify(previousActivity.meta));

      this._simulatingActivityMap[activity.id] = activity;

      this._store.dispatch(new UpdateOngoingActivityState({
        websiteId: activity.websiteId,
        entity: activity
      }));
    }, 100);
  }

  private generateSecurityScanPendingActivity(activityId: string, websiteId: number): IActivity {
    const activity: IActivity = <IActivity>{};
    activity.id = activityId;
    activity.websiteId = websiteId;
    activity.service = 'manager';
    activity.type = ActivityType.ACTIVITY;
    activity.action = ActivityAction.SECURITY_SCAN;
    activity.date = new Date();
    activity.status = ActivityStatus.PENDING;
    activity.progress = 0;
    activity.message = '';
    activity.meta = null;

    return activity;
  }

  private generateSecurityScanFailedActivity(activityId: string, websiteId: number): IActivity {
    const activity: IActivity = <IActivity>{};
    activity.id = activityId;
    activity.websiteId = websiteId;
    activity.service = 'manager';
    activity.type = ActivityType.ACTIVITY;
    activity.action = ActivityAction.SECURITY_SCAN;
    activity.date = new Date();
    activity.status = ActivityStatus.FAILED;
    activity.progress = 100;
    activity.message = '';
    activity.meta = null;

    return activity;
  }

}
